import React, { MouseEvent as ReactMouseEvent, RefObject, useEffect, useRef, useState, forwardRef, useContext, PropsWithChildren } from 'react';
import { styled } from '@mui/material/styles';
import { WindowController, WindowBind } from './window.controller';
import { ResizeDirection } from './windows.context';
import { WindowsContext } from './WindowsContextProvider';

const VertDragger = styled('div')({
  position: 'absolute',
  width: '4px',
  right: '-4px',
  top: 0,
  bottom: 0,
  cursor: 'ew-resize',
});
const RightVertDragger = styled(VertDragger)({
  right: '-4px'
});
const LeftVertDragger = styled(VertDragger)({
  left: '-4px'
});

const HorDragger = styled('div')({
  position: 'absolute',
  height: '4px',
  right: 0,
  left: 0,
  cursor: 'ns-resize',
});
const TopHorDragger = styled(HorDragger)({
  top: '-4px',
});
const BottomHorDragger = styled(HorDragger)({
  bottom: '-4px',
});

const BothDragger = styled('div')({
  position: 'absolute',
  height: '15px',
  width: '15px',
})
const TopLeftBothDragger = styled(BothDragger)({
  top: '-4px',
  left: '-4px',
  cursor: 'nwse-resize',
});
const TopRightBothDragger = styled(BothDragger)({
  top: '-4px',
  right: '-4px',
  cursor: 'nesw-resize',
});
const BottomLeftBothDragger = styled(BothDragger)({
  bottom: '-4px',
  left: '-4px',
  cursor: 'nesw-resize',
});
const BottomRightBothDragger = styled(BothDragger)({
  bottom: '-4px',
  right: '-4px',
  cursor: 'nwse-resize',
});

type WindowProps = {
  header: string;
  opened?: boolean;
  bind?: WindowBind;
  initialWidth: number,
  initialHeight: number,
  minWidth: number,
  minHeight: number
};

type PrivateData = {
  header: Element | null;
  dragFulcrum: { x: number, y: number } | null;
  headerCapture: { x: number, y: number } | null;
  headerMouseDown: (e: MouseEvent) => void;
  headerMouseUp: (e: MouseEvent) => void;
  headerMouseMove: (e: MouseEvent) => void;
  drag: (e: MouseEvent) => void;
  stopDrag: (e: MouseEvent) => void;
  startResize: (d: ResizeDirection) => (e: ReactMouseEvent) => void;
  resize: (e: MouseEvent) => void;
  stopResize: (e: MouseEvent) => void;
  direction: ResizeDirection;
};

export default forwardRef((
  {
    opened = false, bind, header, children,
    initialWidth, initialHeight, minWidth, minHeight
  }: WindowProps & PropsWithChildren,
  mainRef: RefObject<HTMLDivElement | null>
) => {
  if (!mainRef) {
    mainRef = useRef<HTMLDivElement | null>(null);
  }

  const windowsContext = useContext(WindowsContext);

  const [controller] = useState<WindowController>(new WindowController(bind));

  const [data] = useState<PrivateData>({
    header: null,
    headerCapture: null,
    dragFulcrum: null,
    direction: ResizeDirection.None,
    headerMouseDown: (e: MouseEvent) => {
      if ((getComputedStyle(e.target as HTMLElement).userSelect === 'none') && e.button === 0) {
        data.headerCapture = { x: e.clientX, y: e.clientY };
      } else if (e.button === 0) {
        e.stopPropagation();
      }
    },
    headerMouseUp: (e: MouseEvent) => {
      data.headerCapture = null;
    },
    headerMouseMove: (e: MouseEvent) => {
      if (
        !!data.headerCapture &&
        !data.dragFulcrum &&
        e.button == 0
      ) {
        const dist = Math.sqrt(Math.pow(e.clientX - data.headerCapture.x, 2) + Math.pow(e.clientY - data.headerCapture.y, 2));
        if (dist > 3) {
          document.body.classList.toggle('lock-mouse', true);
          window.addEventListener('mousemove', data.drag);
          window.addEventListener('mouseup', data.stopDrag);
          e.preventDefault();
          e.stopPropagation();
          data.dragFulcrum = data.headerCapture;
        }
      }
    },
    drag: (e: MouseEvent) => {
      const cursor = { x: e.clientX, y: e.clientY };
      if (data.dragFulcrum && windowsContext.isInside(cursor)) {
        const delta = {
          x: cursor.x - data.dragFulcrum.x,
          y: cursor.y - data.dragFulcrum.y
        };
        if (delta.x || delta.y) {
          windowsContext.move(controller, delta);
          data.dragFulcrum = cursor;
        }
      }
    },
    stopDrag: (e: MouseEvent) => {
      window.removeEventListener('mousemove', data.drag);
      window.removeEventListener('mouseup', data.stopDrag);
      data.headerCapture = null;
      data.dragFulcrum = null;
      document.body.classList.toggle('lock-mouse', false);
    },
    startResize: (d: ResizeDirection) => (event: ReactMouseEvent) => {
      if (event.button == 0 && mainRef.current) {
        document.body.classList.toggle('lock-mouse', true);

        window.addEventListener('mousemove', data.resize);
        window.addEventListener('mouseup', data.stopResize);
        data.direction = d;
        data.dragFulcrum = { x: event.clientX, y: event.clientY };
        event.preventDefault();
        event.stopPropagation();
      }
    },
    resize: (e: MouseEvent) => {
      const cursor = { x: e.clientX, y: e.clientY };
      if (data.direction && data.dragFulcrum && windowsContext.isInside(cursor)) {
        const delta = {
          x: cursor.x - data.dragFulcrum.x,
          y: cursor.y - data.dragFulcrum.y
        };
        if (delta.x || delta.y) {
          windowsContext.resize(controller, delta, data.direction);
          data.dragFulcrum.x = cursor.x;
          data.dragFulcrum.y = cursor.y;
        }
      }
    },
    stopResize: () => {
      window.removeEventListener('mousemove', data.resize);
      window.removeEventListener('mouseup', data.stopResize);
      data.direction = ResizeDirection.None;
      data.dragFulcrum = null;
      document.body.classList.toggle('lock-mouse', false);
    },
  });

  useEffect(() => {
    if (mainRef.current) {
      controller.element = mainRef.current;      
      const raiseHandler = () => windowsContext.raise(controller);
      mainRef.current.addEventListener('mousedown', raiseHandler);
      return () => {
        if (mainRef.current) {
          mainRef.current.removeEventListener('mousedown', raiseHandler);
        }
        windowsContext.remove(controller);
        controller.element = null;
      }
    }
  }, [mainRef.current]);
  
  useEffect(() => {
    const h = document.querySelector(header);
    if (h) {
      data.header = h;
      data.header.addEventListener('mousedown', data.headerMouseDown);
      data.header.addEventListener('mouseup', data.headerMouseUp);
      data.header.addEventListener('mousemove', data.headerMouseMove);
      return () => {
        if (data.header) {
          data.header.removeEventListener('mousedown', data.headerMouseDown);
          data.header.removeEventListener('mouseup', data.headerMouseUp);
          data.header.removeEventListener('mousemove', data.headerMouseMove);
          data.header = null;
        }
      }
    }
  }, [ header ]);

  useEffect(() => {
    if (opened) {
      windowsContext.add(controller);
      return () => {
        windowsContext.reduce(controller);
      }
    }
  }, [ opened ]);

  return (
    <div
      ref={mainRef}
      style={{
        display: opened ? 'block' : 'none',
        position: 'absolute',
        width: initialWidth,
        height: initialHeight,
        minWidth,
        minHeight,
      }}
    >
      {children}
      <RightVertDragger onMouseDown={data.startResize(ResizeDirection.Right)} />
      <LeftVertDragger onMouseDown={data.startResize(ResizeDirection.Left)} />
      <TopHorDragger onMouseDown={data.startResize(ResizeDirection.Top)} />
      <BottomHorDragger onMouseDown={data.startResize(ResizeDirection.Bottom)} />
      <TopLeftBothDragger onMouseDown={data.startResize(ResizeDirection.Top | ResizeDirection.Left)} />
      <TopRightBothDragger onMouseDown={data.startResize(ResizeDirection.Top | ResizeDirection.Right)} />
      <BottomLeftBothDragger onMouseDown={data.startResize(ResizeDirection.Bottom | ResizeDirection.Left)} />
      <BottomRightBothDragger onMouseDown={data.startResize(ResizeDirection.Bottom | ResizeDirection.Right)} />
    </div>
  );
});